import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import { generateClient } from 'aws-amplify/api';
import { useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../../amplify/data/resource';
import EmployeesIcon from '../../../../components/activeIcons/EmployeesIcon';
import { BankInfo } from '../../../../components/custom/BankInfo';
import { FundLogo } from '../../../../components/custom/FundLogo';
import ContributionsTable from '../../../../components/ContributionsTable/ContributionsTable';
import {
  extractCompanyId,
  extractFundNumber,
  formatMoney,
} from '../../../../utils/format/format';
import { getFundLogo } from '../../../../utils/logos/logos';

const sidebarVariants = {
  hidden: { x: '-100%', transition: { duration: 0.5 } },
  visible: { x: 0, transition: { duration: 0.5 } },
};

interface PaymentSidebarProps {
  open: boolean;
  onClose: () => void;
  depositId: string;
}

const apiClient = generateClient<Schema>();
const PaymentSidebar: React.FC<PaymentSidebarProps> = ({
  open,
  onClose,
  depositId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [deposit, setDeposit] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDeposit = async () => {
      if (depositId) {
        setLoading(true);
        try {
          const response = await apiClient.models.Deposit.get(
            { id: depositId },
            {
              selectionSet: [
                'id',
                'status',
                'fundIdentifier',
                'depositorType',
                'employerIdentifierType',
                'employerId',
                'deductionAccountNumber',
                'employerCreatorIdentifier',
                'statusCode',
                'totalDepositAmount',
                'employerName',
                'employerContactFirstName',
                'employerContactLastName',
                'employerContactPhone',
                'employerContactEmail',
                'employerContactCellular',
                'actionType',
                'paymentMethodCode',
                'fundDepositAmount',
                'depositValueDateFund',
                'depositValueDateAccount',
                'transferReferenceNumber',
                'uniqueIdentifier',
                'employerBankNumber',
                'employerBranchNumber',
                'employerAccountNumber',
                'accountType',
                'employerCardType',
                'employerAccountType',
                'recipientAccountType',
                'recipientBankNumber',
                'recipientBranchNumber',
                'recipientAccountNumber',
                'previousIdentifier',
                'transferNumber',
                'previousTransferNumber',
                'fundType',
                'pensionFundType',
                'fundNameAtEmployer',
                'fundNumberAtEmployer',
                'employerContributionAmount',
                'employerDepositAmount',
                'numberOfParticipants',
                'applicantType',
                'applicantCodeType',
                'applicantIdentifier',
                'applicantName',
                'handlerIdentifier',
                'applicantFirstName',
                'applicantLastName',
                'applicantPhone',
                'applicantEmail',
                'applicantCellular',
                'employees.*',
              ],
            },
          );
          setDeposit(response.data);
        } catch (error) {
          console.error('Error fetching file:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDeposit();
  }, [depositId]);

  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleClosePaymentSidebar = () => {
    onClose();
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
    }, 500);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        handleClosePaymentSidebar();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const [fundLogo, setFundLogo] = useState('');

  useEffect(() => {
    const { fundLogo } = getFundLogo(
      extractFundNumber(deposit?.fundIdentifier) || null,
    );
    setFundLogo(fundLogo);
  }, [deposit?.fundIdentifier]);

  const calculateUniqueEmployees = (employees: any) => {
    if (!Array.isArray(employees) || employees.length === 0) {
      return 0;
    }

    const uniqueEmployeeIds = new Set();

    employees.forEach((employee) => {
      if (employee && employee.employeeId) {
        uniqueEmployeeIds.add(employee.employeeId);
      }
    });

    return uniqueEmployeeIds.size;
  };

  const uniqueCount = calculateUniqueEmployees(deposit?.employees);

  return (
    <motion.div
      ref={sidebarRef}
      initial="hidden"
      animate={open && !isClosing ? 'visible' : 'hidden'}
      exit="hidden"
      variants={sidebarVariants}
      style={{
        direction: 'rtl',
        width: '500px',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#FFF',
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          py: '45px',
          px: '25px',
          display: 'flex',
          flexDirection: 'column',
          gap: '50px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            boxShadow: '0px 0px 50px 0px #00000040',
            flexDirection: 'column',
            gap: '20px',
            py: '20px',
            px: '30px',
            borderRadius: '30px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Icon
                style={{ cursor: 'pointer' }}
                icon={'material-symbols:close'}
                onClick={handleClosePaymentSidebar}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <EmployeesIcon number={uniqueCount} />
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
              >
                <Box sx={{ display: 'flex', gap: '20px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      fontWeight={'700'}
                      fontSize={'14px'}
                      textAlign={'right'}
                      fontFamily={'AlmoniDL'}
                    >
                      {deposit?.fundNameAtEmployer}
                    </Typography>
                    <Typography
                      textAlign={'right'}
                      fontWeight={'700'}
                      fontSize={'14px'}
                      fontFamily={'Montserrat'}
                    >
                      {(() => {
                        const identifier = deposit?.fundIdentifier;

                        const fundNumber = extractFundNumber(identifier);

                        if (fundNumber !== 0) {
                          return fundNumber;
                        } else {
                          return extractCompanyId(identifier);
                        }
                      })()}
                    </Typography>
                  </Box>
                  <FundLogo
                    width="100px"
                    height="50px"
                    fundNumber={deposit?.fundIdentifier}
                  />
                </Box>
                <Typography
                  textAlign={'right'}
                  fontWeight={'700'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                >
                  {deposit?.totalDepositAmount &&
                    formatMoney(deposit?.totalDepositAmount || 0)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '5px',
              }}
            >
              <BankInfo fundNumber={deposit?.fundIdentifier || ''} />
            </Box>
          </Box>
        </Box>
        {deposit?.employees?.map((employee: any, index: number) => (
          <EmployeeItem key={index} employee={employee} />
        ))}
      </Box>
    </motion.div>
  );
};

export default PaymentSidebar;

interface EmployeeItemProps {
  employee: any;
}

const EmployeeItem: React.FC<EmployeeItemProps> = ({ employee }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: '10px',
        gap: '30px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={toggleOpen}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '50px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
              width: '150px',
            }}
          >
            <img alt="greyCard" src="/assets/file/payments/greyCard.svg" />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                textAlign={'right'}
                fontFamily={'AlmoniDL'}
                sx={{
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {employee.firstName} {employee.lastName}
              </Typography>
              <Typography
                textAlign={'right'}
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'Montserrat'}
              >
                {employee.employeeId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '5px' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                textAlign={'right'}
                fontFamily={'AlmoniDL'}
              >
                סכום להפקדה
              </Typography>
              <Typography
                textAlign={'right'}
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'Montserrat'}
              >
                {formatMoney(
                  employee?.employeeContributionAllocation?.reduce(
                    (total: number, contribution: any) =>
                      total + contribution?.contributionAmount,
                    0,
                  ) || 0,
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
        <img
          alt="arrow"
          src="/assets/file/payments/arrow.svg"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </Box>

      {isOpen && (
        <Box sx={{ mt: '20px' }}>
          <ContributionsTable
            contributions={employee.employeeContributionAllocation}
          />
        </Box>
      )}
    </Box>
  );
};
